import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Feather, Sparkles, Loader2, Download, Save, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API, saveSaggioToCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SampleProtocol() {
  const { isAdmin, loading: authLoading } = useAuth();
  const [req, setReq] = useState({
    signature_type: "estesa", include_numbers: false, include_text: false,
    substrate: "", signature_position: "", particularities: "", pen_color: "blu",
    include_assegni: false,
  });
  const [meta, setMeta] = useState({
    procedimento: "", soggetto: "", luogo: "", data: "",
    doc_identita: "", rilasciato_da: "",
  });
  const [caseId, setCaseId] = useState("");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const c = searchParams.get("case");
    if (c) setCaseId(c);
  }, [searchParams]);
  const [useAI, setUseAI] = useState(true);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [savingToCase, setSavingToCase] = useState(false);

  const saveToCase = async () => {
    if (!caseId.trim()) {
      toast.error("Inserisci l'ID del fascicolo per salvare");
      return;
    }
    setSavingToCase(true);
    try {
      const r = await saveSaggioToCase(caseId.trim(), {
        request: req, meta,
        use_ai_adaptation: useAI,
        label: meta.soggetto ? `Saggio ${meta.soggetto}` : undefined,
      });
      toast.success("Saggio salvato nel fascicolo");
      window.open(`/case/${caseId.trim()}`, "_blank");
    } catch (e) {
      const msg = e?.response?.status === 404 ? "Fascicolo non trovato" : "Errore nel salvataggio";
      toast.error(msg);
    } finally {
      setSavingToCase(false);
    }
  };

  const generateConfig = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/saggio-grafico/generate", {
        request: req, meta, case_id: caseId || null, use_ai_adaptation: useAI,
      });
      setConfig(r.data.config);
      toast.success(useAI ? "Protocollo adattato con AI" : "Protocollo generato");
    } catch {
      toast.error("Errore nella generazione");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/saggio-grafico/pdf`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ request: req, meta, case_id: caseId || null, use_ai_adaptation: useAI }),
      });
      if (res.status === 401 || res.status === 403) {
        toast.error("Accedi come amministratore per generare il PDF");
        return;
      }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "saggio-grafico.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      {!authLoading && !isAdmin && (
        <main className="flex-1 flex items-center justify-center px-6" data-testid="saggio-forbidden">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Strumento riservato</h1>
            <p className="text-sm text-slate-600 mt-2">Il configuratore del Saggio grafico è disponibile solo agli amministratori.</p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
      )}
      {isAdmin && (
      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div className="overline mb-2 flex items-center gap-2"><Feather size={14} /> Saggio grafico</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Protocollo del saggio grafico</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Configuratore del saggio da eseguire in presenza. Genera un PDF stampabile
          (modello Dott. Lugliè) con Sezioni A-H personalizzate sul caso concreto — l'AI
          adatta premessa, numero delle firme e istruzioni alle particolarità.
        </p>

        <form onSubmit={generateConfig} className="border hairline bg-white p-6 lg:p-10 mt-8 space-y-8">
          {/* Dati soggetto */}
          <section>
            <div className="overline mb-4">Dati del soggetto e del procedimento</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Procedimento n."><Input value={meta.procedimento} onChange={(v) => setMeta({ ...meta, procedimento: v })} testid="sp-procedimento" /></Field>
              <Field label="Nominativo soggetto"><Input value={meta.soggetto} onChange={(v) => setMeta({ ...meta, soggetto: v })} testid="sp-soggetto" /></Field>
              <Field label="Luogo"><Input value={meta.luogo} onChange={(v) => setMeta({ ...meta, luogo: v })} testid="sp-luogo" /></Field>
              <Field label="Data prevista"><Input type="date" value={meta.data} onChange={(v) => setMeta({ ...meta, data: v })} testid="sp-data" /></Field>
              <Field label="Documento identità n."><Input value={meta.doc_identita} onChange={(v) => setMeta({ ...meta, doc_identita: v })} testid="sp-doc" /></Field>
              <Field label="Rilasciato da"><Input value={meta.rilasciato_da} onChange={(v) => setMeta({ ...meta, rilasciato_da: v })} testid="sp-rilasciato" /></Field>
            </div>
          </section>

          {/* Configurazione tecnica */}
          <section>
            <div className="overline mb-4">Configurazione del saggio</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Tipologia firma">
                <select value={req.signature_type} onChange={(e) => setReq({ ...req, signature_type: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="sp-signature-type">
                  <option value="estesa">Firma estesa</option>
                  <option value="sigla">Sigla</option>
                  <option value="iniziali">Iniziali</option>
                </select>
              </Field>
              <Field label="Colore penna">
                <select value={req.pen_color} onChange={(e) => setReq({ ...req, pen_color: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="sp-pen-color">
                  <option value="blu">Blu</option>
                  <option value="nero">Nero</option>
                </select>
              </Field>
              <Field label="Supporto / posizione">
                <Input value={req.substrate} onChange={(v) => setReq({ ...req, substrate: v })} placeholder="Es. A4, modulo bancario..." testid="sp-substrate" />
              </Field>
              <Field label="ID fascicolo Perizia Ready (facoltativo)">
                <Input value={caseId} onChange={setCaseId} placeholder="incolla l'ID del caso per adattamento contestuale" testid="sp-caseid" />
              </Field>
              <div className="md:col-span-2">
                <Field label="Particolarità del caso">
                  <Input value={req.particularities} onChange={(v) => setReq({ ...req, particularities: v })} placeholder="Es. età avanzata, tremore, ricoveri recenti, mancinismo..." testid="sp-particularities" />
                </Field>
              </div>
              <div className="flex flex-wrap gap-6 md:col-span-2">
                <Check label="Includere numeri" checked={req.include_numbers} onChange={(v) => setReq({ ...req, include_numbers: v })} testid="sp-numbers" />
                <Check label="Includere testo manoscritto" checked={req.include_text} onChange={(v) => setReq({ ...req, include_text: v })} testid="sp-text" />
                <Check label="Includere sezioni assegni (I, L, M, N)" checked={req.include_assegni} onChange={(v) => setReq({ ...req, include_assegni: v })} testid="sp-assegni" />
                <Check label="Adattamento AI (Claude)" checked={useAI} onChange={setUseAI} testid="sp-use-ai" />
              </div>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <button type="submit" disabled={loading}
              className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="sp-preview-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Anteprima protocollo
            </button>
            {caseId && (
              <button type="button" onClick={saveToCase} disabled={savingToCase}
                className="inline-flex items-center gap-2 border border-forensic-green text-forensic-green hover:bg-forensic-green hover:text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
                data-testid="sp-save-to-case-btn">
                {savingToCase ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salva nel fascicolo
              </button>
            )}
            <button type="button" onClick={downloadPdf} disabled={pdfLoading}
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-60"
              data-testid="sp-download-btn">
              {pdfLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Scarica PDF stampabile
            </button>
          </div>
        </form>

        {config && (
          <div className="border hairline bg-white p-6 lg:p-10 mt-6 space-y-6" data-testid="config-result">
            <div>
              <div className="overline mb-2">Premessa</div>
              <p className="text-sm text-slate-700 leading-relaxed">{config.premise}</p>
            </div>
            {config.ai_notes && (
              <div className="border-l-2 border-navy pl-4">
                <div className="overline mb-2">Note AI di adattamento</div>
                <p className="text-sm text-slate-700 leading-relaxed">{config.ai_notes}</p>
              </div>
            )}
            <div>
              <div className="overline mb-3">Sezioni</div>
              <div className="space-y-3">
                {config.sections.map((s) => (
                  <div key={s.id} className="border hairline p-4 bg-paper" data-testid={`section-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-navy">SEZIONE {s.id}</span>
                      <h3 className="font-serif text-lg font-semibold">{s.title}</h3>
                      {s.count > 0 && <span className="ml-auto text-xs font-mono text-slate-500">n. {s.count}</span>}
                    </div>
                    <p className="mt-2 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{s.instructions}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      )}
      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><div className="overline mb-2">{label}</div>{children}</label>;
}
function Input({ value, onChange, type = "text", placeholder, testid }) {
  return <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid={testid} />;
}
function Check({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-navy" data-testid={testid} />
      {label}
    </label>
  );
}
